import jwtDecode from 'jwt-decode';
import format from 'date-fns/format';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Grid, Stack, useTheme, Typography } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';
import { updateUser } from 'src/services/user.service';
// WITH THIS IMPORT:
import { fetchDepartments } from 'src/services/department.service';

import { styles } from './styles';
import UserDetails from './subSections/UserDetails';
import UserInfoForm from './subSections/UserInfoForm';
import AvatarSection from './subSections/AvatarSection';
import { convertLocalTime } from '../../utils/convertTime';
import PermissionsMatrix from './subSections/PermissionMatrix';
import ChangePasswordSection from './subSections/ChangePasswordSection';
import UpdateProfilePictureDialog from './dialog/UpdateProfilePictureDialog';

export default function ProfileView() {
  const { user, fetchUser, token } = useAuth();
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { showToast, hideToast } = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [userDepartments, setUserDepartments] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');

  useEffect(() => {
    let activeDepts = [];
    if (token) {
      try {
        const decoded = jwtDecode(token);
        activeDepts = decoded?.uniqueDepartmentIds || [];
      } catch (e) {
        console.error('Failed to decode token on profile view:', e);
      }
    }

    if (user?.departments?.length > 0) {
      setUserDepartments(user.departments);
    } else if (activeDepts.length > 0) {
      getUserDepartments(activeDepts);
    } else {
      setUserDepartments([]);
    }

    if (user?.createdAt) {
      setStartDate(
        format(convertLocalTime(new Date(user.createdAt)), 'dd/MM/yyyy HH:mm:ss')
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const modifyUser = async (newData) => {
    try {
      await updateUser(user.id, {
        id: user.id,
        givenName: newData.givenName,
        familyName: newData.familyName,
        username: newData.username,
        email: newData.email,
        phoneNumber: newData.phoneNumber,
        avatar: newData.avatar,
        role: user.role,
        departmentIds: user.departmentIds,
      });

      await fetchUser(token);
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  };

  const getUserDepartments = async (activeDepts) => {
    genericApiToaster(
      fetchDepartments,
      [],
      '',
      t('errorMessages.couldntLoadUser'),
      showToast,
      hideToast,
      t,
      (res) => {
        const matchedDeps = (res || []).filter((dep) => activeDepts.includes(dep.id));
        setUserDepartments(matchedDeps);
      }
    );
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <UpdateProfilePictureDialog
        dialogOpen={dialogOpen}
        handleDialogClose={handleDialogClose}
        modifyUser={modifyUser}
        userData={user}
      />
      <Grid container flexGrow={1} justifyContent="center">
        <Grid
          item
          xs={10.7}
          sm={7.7}
          sx={{ ...styles.infoBox, p: isSmallScreen ? 1 : 2 }}
        >
          <Stack spacing={2}>
            <AvatarSection setDialogOpen={setDialogOpen} />

            <Box sx={styles.tabHeader}>
              <Box
                onClick={() => setActiveTab('edit')}
                sx={{
                  ...styles.tabButton,
                  ...(activeTab === 'edit' && styles.activeTab),
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/Tickets/Edit.svg"
                  sx={{
                    width: 18,
                    filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                    transition: 'filter 0.25s',
                  }}
                />
                <Typography variant="subtitle2">
                  {t('permissions.editProfile')}
                </Typography>
              </Box>

              <Box
                onClick={() => setActiveTab('perms')}
                sx={{
                  ...styles.tabButton,
                  ...(activeTab === 'perms' && styles.activeTab),
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/Tickets/permission.svg"
                  sx={{
                    width: 18,
                    filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                    transition: 'filter 0.25s',
                  }}
                />
                <Typography variant="subtitle2">{t('permissions.title')}</Typography>
              </Box>
            </Box>

            <Box sx={{ p: 1 }}>
              {activeTab === 'edit' ? (
                <UserInfoForm modifyUser={modifyUser} />
              ) : (
                <PermissionsMatrix />
              )}
            </Box>
          </Stack>
        </Grid>

        <Grid
          item
          xs={10.7}
          sm={3.6}
          sx={{ ...styles.infoBox, p: isSmallScreen ? 2 : 3 }}
        >
          <Stack spacing={3}>
            <UserDetails departments={userDepartments} startDate={startDate} />
            <ChangePasswordSection userId={user.id} />
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}
